import * as maplibregl from 'maplibre-gl'
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { firstLabelLayerId, HEAT_PAINT } from '../components/HeatmapLayer'
import {
  ANNOTATION_POPUP_WIDTH_PX,
  AnnotationPopupContent,
} from './AnnotationPopupContent'
import { annotationSeverityColor } from './annotation-labels'
import {
  ANNOTATION_MARKER_SIZE_PX,
  ANNOTATION_POP_IN,
  ANNOTATION_POP_OUT,
  ANNOTATION_POP_OUT_MS,
  ANNOTATION_POPUP_CLASS,
  annotationMarkerAriaLabel,
  createAnnotationMarkerElement,
  setAnnotationMarkerColor,
  setAnnotationMarkerResolved,
} from './annotation-marker-element'
import {
  buildAnnotationHeatFeatures,
  filterPlottableAnnotations,
} from './annotation-utils'
import type { TripAnnotationMarker } from './types'

// Named `-heat`, matching the overwatch heat layers, and deliberately NOT ending in
// `-line`, `-fill`, or `-outline`: route-builder's updateRouteLine sweeps every layer
// with one of those suffixes beneath the route line, which would drag the heatmap
// around with the lane and geonet overlays it means to reorder.
const ANNOTATION_HEAT_SOURCE_ID = 'annotation-heat-source'
const ANNOTATION_HEAT_LAYER_ID = 'annotation-heat'

// Grace period between the pointer leaving the marker/popup and the popup closing,
// so a pointer travelling from one to the other doesn't close it mid-journey.
const POPUP_CLOSE_GRACE_MS = 150

// One entry per currently-plotted annotation marker: `container` is the popup's
// (otherwise empty) DOM node, which `AnnotationPopupContent` gets portaled into
// from the caller's own render tree rather than mounted as a second React root.
type AnnotationPopupEntry = {
  id: string
  container: HTMLDivElement
  annotation: TripAnnotationMarker
}

// One live annotation marker, keyed by annotation id so `updateMarkers` can diff
// frame-to-frame instead of tearing every marker down: only entering ids pop in
// and only leaving ids pop out, so persisting markers stay put (and their popups
// stay open) across a filter change. `inner` is the animated child (see
// `createAnnotationMarkerElement` — the pop scale can't live on the
// MapLibre-owned outer element); `dispose` detaches this marker's hover
// listeners and cancels its pending close timer; `removalTimeoutId` is set only
// while a departed marker is mid pop-out, so a re-entering id can cancel it and
// revive instead of stacking a second exit.
type AnnotationMarkerEntry = {
  marker: maplibregl.Marker
  el: HTMLElement
  inner: HTMLElement
  container: HTMLDivElement
  popup: maplibregl.Popup
  dispose: () => void
  removalTimeoutId?: ReturnType<typeof setTimeout>
}

export type UseAnnotationMapLayerOptions = {
  /**
   * The live MapLibre instance, or null before it exists. Safe to pass a map that
   * hasn't finished loading its style — the layer waits for `load` itself.
   */
  map: maplibregl.Map | null
  /** The annotations to draw, already filtered (see `useAnnotationsState`). */
  annotations: TripAnnotationMarker[]
  /** Also draw the same annotations as a density heatmap beneath the markers. */
  showHeatmap?: boolean
  /**
   * Keep the heat layer directly beneath this layer, when it exists — normally
   * the map's route line, so the heat sits above the lane/geonet/ODD overlays but
   * under the route. Falls back to just under the basemap labels.
   */
  belowLayerId?: string
  /** Forwarded to the popup — see `AnnotationPopupContent.buildTripHref`. */
  buildTripHref?: (tripId: string) => string | null
  /**
   * Colours each marker glyph. Defaults to severity, which is what route-builder
   * and trip-history draw; the annotation-triage page colours by who labelled the
   * note instead, because on that page severity is a table column and provenance
   * is the thing a reviewer scans the map for.
   *
   * Pass a stable function (module-level or memoised): a new identity re-runs the
   * marker pass. Cheap — persisting markers are recoloured in place, not rebuilt —
   * but pointless on every render.
   */
  markerColor?: (annotation: TripAnnotationMarker) => string
  /** Forwarded to the popup — see `AnnotationPopupContent.renderDetails`. */
  renderPopupDetails?: (annotation: TripAnnotationMarker) => ReactNode
}

const severityMarkerColor = (annotation: TripAnnotationMarker) =>
  annotationSeverityColor(annotation.severity)

/**
 * Attaches the driver-voice-note layer — markers (severity-coloured unless the
 * caller passes `markerColor`), their hover popups, and the optional density
 * heatmap — to any MapLibre map.
 *
 * All of the DOM/GL glue that used to live inside route-builder's `RouteMap`
 * lives here instead, so a second map (trip-history) gets the identical layer by
 * calling this hook rather than by re-implementing marker diffing, popup
 * reparenting and heat z-ordering. The caller renders the returned
 * `annotationLayer` node as a **sibling of its map container** (see the comment
 * on the overlay div below for why a sibling specifically) and passes the map
 * instance in.
 */
export const useAnnotationMapLayer = ({
  map,
  annotations,
  showHeatmap = false,
  belowLayerId,
  buildTripHref,
  markerColor = severityMarkerColor,
  renderPopupDetails,
}: UseAnnotationMapLayerOptions) => {
  const { t } = useTranslation()
  // Sibling escape hatch for the annotation hover popup -- see the comment on
  // the div this is attached to, and the one in `openPopup`, for why the popup's
  // DOM element gets reparented in here on every open.
  const popupOverlayRef = useRef<HTMLDivElement>(null)
  // Keyed by annotation id (not an array) so `updateMarkers` can diff against
  // the previous pass and animate only the difference — see AnnotationMarkerEntry.
  const markersRef = useRef<Map<string, AnnotationMarkerEntry>>(new Map())
  // Pending "close after leaving" timers from the hover handlers below -- tracked
  // so a fresh `updateMarkers` pass (or unmount) can cancel any timer whose
  // marker/popup it's about to remove, instead of letting a stale timeout fire
  // `togglePopup` on a marker that's no longer on the map.
  const timeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set())
  // State, not a ref: this drives the `createPortal` calls in the returned node,
  // so React needs to know it changed. Recomputed on every `updateMarkers` pass
  // to mirror the current marker set (one entry per plotted annotation, carrying
  // the latest annotation data so the portaled popup body re-renders), while the
  // markers themselves are diffed in place.
  const [popups, setPopups] = useState<AnnotationPopupEntry[]>([])
  // Gates the *heatmap* only: `addSource`/`addLayer` throw before the style is
  // queryable. The DOM markers deliberately don't wait on this -- see the
  // `updateMarkers` effect.
  const [isMapReady, setIsMapReady] = useState(false)

  useEffect(() => {
    if (!map) {
      setIsMapReady(false)
      return
    }
    if (map.isStyleLoaded()) {
      setIsMapReady(true)
      return
    }
    // NOT `map.once('load')`: both callers publish their instance from inside
    // their own `load` handler, so by the time this effect runs `load` has
    // already fired and will never fire again — waiting on it would leave the
    // layer permanently off. `isStyleLoaded()` is still false at that point
    // whenever the style has pending work (the route/lane/ODD sources those
    // handlers add, sprites, glyphs), so readiness has to be re-checked on an
    // event that repeats. `styledata` fires on every style mutation and `idle`
    // after the map settles, so between them one always lands post-load.
    const check = () => {
      if (!map.isStyleLoaded()) return
      setIsMapReady(true)
      map.off('styledata', check)
      map.off('idle', check)
    }
    map.on('styledata', check)
    map.on('idle', check)
    return () => {
      map.off('styledata', check)
      map.off('idle', check)
    }
  }, [map])

  // Keep the heat layer directly beneath `belowLayerId`, i.e. above the lane,
  // geonet and ODD overlays. useMapOverlays' applyLayerZOrder re-floats those
  // overlays to just under the route line every time they sync, which would
  // otherwise bury the heat that was sitting there — so this is re-asserted from a
  // `styledata` listener (below) as well as right after the layer is (re)built.
  // Idempotent on purpose: it only calls moveLayer when the layer is out of
  // position, so the styledata its own move emits finds it correct and stops,
  // rather than looping. With no route line yet the overlays float to the top of
  // the GL stack (their beforeId is the absent route line), so the heat goes there
  // too. DOM markers always paint above every GL layer, so this never hides them.
  const positionHeatmap = useCallback(
    (currentMap: maplibregl.Map) => {
      if (!currentMap.getLayer(ANNOTATION_HEAT_LAYER_ID)) return
      const style = currentMap.getStyle()
      if (!style) return
      const heatIdx = style.layers.findIndex(
        l => l.id === ANNOTATION_HEAT_LAYER_ID,
      )
      if (heatIdx === -1) return
      if (belowLayerId && currentMap.getLayer(belowLayerId)) {
        const belowIdx = style.layers.findIndex(l => l.id === belowLayerId)
        if (heatIdx === belowIdx - 1) return
        currentMap.moveLayer(ANNOTATION_HEAT_LAYER_ID, belowLayerId)
        return
      }
      if (heatIdx === style.layers.length - 1) return
      currentMap.moveLayer(ANNOTATION_HEAT_LAYER_ID)
    },
    [belowLayerId],
  )

  // Driver voice notes, as a density heatmap over the lane/geonet overlays but
  // below the route line. Answers a different question than the pins do: the pins
  // say "a note was left here", the heat says "notes keep being left around here",
  // which is what makes a corner worth a second look. Severity rides along in
  // `heatmap-weight` rather than in the colour ramp — the ramp already means
  // density, and a second meaning on the same channel would read as severity when
  // it is showing volume. The markers carry the severity colour.
  const updateHeatmap = useCallback(
    (
      currentMap: maplibregl.Map,
      currentAnnotations: TripAnnotationMarker[],
      enabled: boolean,
    ) => {
      // Toggling off feeds an empty collection instead of removing the layer: the
      // source and layer are created once and then only ever have their data
      // swapped, so there is no remove/add race with the load-gated effect below.
      const geojson = enabled
        ? buildAnnotationHeatFeatures(currentAnnotations)
        : { type: 'FeatureCollection' as const, features: [] }

      const source = currentMap.getSource(ANNOTATION_HEAT_SOURCE_ID) as
        | maplibregl.GeoJSONSource
        | undefined
      if (source) {
        source.setData(geojson)
        return
      }

      // Nothing to draw yet and no layer to keep — don't add a source on the first
      // pass with the toggle off, so a user who never opens the layer pays nothing.
      if (!enabled) return

      currentMap.addSource(ANNOTATION_HEAT_SOURCE_ID, {
        type: 'geojson',
        data: geojson,
      })
      currentMap.addLayer(
        {
          id: ANNOTATION_HEAT_LAYER_ID,
          type: 'heatmap',
          source: ANNOTATION_HEAT_SOURCE_ID,
          paint: {
            ...HEAT_PAINT,
            // Per-note weight by severity, where the other heat layers use a flat 0.5.
            'heatmap-weight': ['get', 'weight'],
            // An isolated point peaks at `weight * intensity` on the shared colour
            // ramp, and the shared 0.6 is calibrated for layers plotting thousands of
            // points: it puts every single note between 0.15 and 0.51, all of which is
            // the dark-purple-to-dark-blue bottom of the ramp and indistinguishable at
            // 55% opacity over a basemap. This layer plots dozens, so 1.0 spreads one
            // note across the ramp (LOW purple, MED blue, HIGH green) and reserves the
            // yellow top for notes that actually pile up on the same block.
            'heatmap-intensity': 1,
          },
        },
        // Slot it just below the route line so it sits above the lane/geonet
        // overlays; positionHeatmap then keeps it there. Before the route line
        // exists, fall back to just under the basemap labels.
        belowLayerId && currentMap.getLayer(belowLayerId)
          ? belowLayerId
          : firstLabelLayerId(currentMap),
      )
      positionHeatmap(currentMap)
    },
    [belowLayerId, positionHeatmap],
  )

  useEffect(() => {
    if (!map || !isMapReady) return
    updateHeatmap(map, annotations, showHeatmap)
  }, [annotations, isMapReady, map, showHeatmap, updateHeatmap])

  // Re-assert the heat layer's z-position whenever the style's layer order
  // changes. The layers that would bury it — lanes, geonets, ODD — are added and
  // re-floated by useMapOverlays, often from a sibling component this layer can't
  // hook into directly, and each such move fires `styledata`, so this catches
  // them. positionHeatmap is idempotent, so the move it makes here re-fires
  // styledata once and then settles.
  useEffect(() => {
    if (!map) return
    const reposition = () => positionHeatmap(map)
    map.on('styledata', reposition)
    return () => {
      map.off('styledata', reposition)
    }
  }, [map, positionHeatmap])

  // Driver voice notes. Markers only — no line layer, since an annotation is a point
  // in time rather than a stretch of road.
  const updateMarkers = useCallback(
    (
      currentMap: maplibregl.Map,
      currentAnnotations: TripAnnotationMarker[],
    ) => {
      const entries = markersRef.current

      // Wires this marker's hover behaviour and returns a `dispose` that undoes
      // it. Kept as a closure per marker because `closeTimeoutId` is per-marker
      // state; hoisting it lets both a departing marker (pop-out) and a
      // reviving one re-attach/detach the same handlers without duplicating
      // this logic inline in three places.
      //
      // Hovering the marker opens the popup; hovering *away* from it used to
      // close it immediately, even when the pointer was moving onto the popup
      // itself (to press play, open the trip link, or copy the id) rather than
      // away from the annotation entirely. Fix: listen on both the marker and
      // the popup's own content container, and give the pointer a short grace
      // period to land on whichever of the two it's heading to before actually
      // closing -- entering either one cancels any pending close.
      const wireHover = (
        el: HTMLElement,
        container: HTMLDivElement,
        marker: maplibregl.Marker,
        popup: maplibregl.Popup,
      ) => {
        let closeTimeoutId: ReturnType<typeof setTimeout> | undefined

        const openPopup = () => {
          if (closeTimeoutId !== undefined) {
            clearTimeout(closeTimeoutId)
            timeoutsRef.current.delete(closeTimeoutId)
            closeTimeoutId = undefined
          }
          el.style.zIndex = '300'
          if (!popup.isOpen()) marker.togglePopup()
          const popupEl = popup.getElement()
          if (popupEl) {
            // Root cause (confirmed from the reported screenshots): the thing
            // painting over the popup's transcript is a numbered *waypoint
            // marker* on the map itself -- the blue "17" circle -- not a side
            // editor panel. MapLibre appends markers into
            // `.maplibregl-canvas-container` and popups into the map container,
            // so both live *inside* the same element. That element sets
            // `isolation: isolate`, making the whole map one atomic stacking
            // context: bumping just this popup's z-index only reorders it
            // *relative to other things inside the map*, and can never lift it
            // above the map as a unit. The robust fix is to move the popup's
            // element out of the map entirely, into the overlay div returned by
            // this hook -- a sibling of the map container that shares the map's
            // exact box, so the popup's `map.project()`-derived transform stays
            // pixel-accurate while now painting above every marker. MapLibre
            // destroys and recreates this element on every close (see
            // `schedulePopupClose`), so the reparenting has to happen on every
            // open, not once. (The in-map z-index below is now just a harmless
            // tiebreaker among popups within the overlay.)
            popupEl.style.zIndex = '9999'
            if (popupEl.parentElement !== popupOverlayRef.current) {
              popupOverlayRef.current?.appendChild(popupEl)
            }
          }
        }

        const schedulePopupClose = () => {
          if (closeTimeoutId !== undefined) {
            clearTimeout(closeTimeoutId)
            timeoutsRef.current.delete(closeTimeoutId)
          }
          const timeoutId = setTimeout(() => {
            timeoutsRef.current.delete(timeoutId)
            closeTimeoutId = undefined
            el.style.zIndex = '150'
            if (popup.isOpen()) marker.togglePopup()
          }, POPUP_CLOSE_GRACE_MS)
          closeTimeoutId = timeoutId
          timeoutsRef.current.add(timeoutId)
        }

        el.addEventListener('mouseenter', openPopup)
        el.addEventListener('mouseleave', schedulePopupClose)
        container.addEventListener('mouseenter', openPopup)
        container.addEventListener('mouseleave', schedulePopupClose)

        return () => {
          el.removeEventListener('mouseenter', openPopup)
          el.removeEventListener('mouseleave', schedulePopupClose)
          container.removeEventListener('mouseenter', openPopup)
          container.removeEventListener('mouseleave', schedulePopupClose)
          if (closeTimeoutId !== undefined) {
            clearTimeout(closeTimeoutId)
            timeoutsRef.current.delete(closeTimeoutId)
            closeTimeoutId = undefined
          }
        }
      }

      // Filtered, not trusted: one row with a swapped lat/lng throws inside
      // setLngLat, and an exception raised here escapes the effect that is drawing
      // the layer, so a single bad annotation would unmount the whole view.
      const plottable = filterPlottableAnnotations(currentAnnotations)
      const desiredIds = new Set(plottable.map(a => a.id))

      // Departed markers: pop them out, then remove once the shrink finishes.
      // Only start the exit for a marker that isn't already exiting, so an id
      // that flickers in and out of the filter can't stack overlapping timers.
      // Detach the hover handlers up front so a stray pointer can't reopen a
      // popup on a marker that's on its way off the map.
      for (const [id, entry] of entries) {
        if (desiredIds.has(id)) continue
        if (entry.removalTimeoutId !== undefined) continue
        entry.dispose()
        entry.inner.style.animation = ANNOTATION_POP_OUT
        const timeoutId = setTimeout(() => {
          timeoutsRef.current.delete(timeoutId)
          entry.marker.remove()
          entries.delete(id)
        }, ANNOTATION_POP_OUT_MS)
        entry.removalTimeoutId = timeoutId
        timeoutsRef.current.add(timeoutId)
      }

      const nextPopups: AnnotationPopupEntry[] = []

      for (const annotation of plottable) {
        const existing = entries.get(annotation.id)

        if (existing) {
          // Was mid pop-out but is wanted again: cancel the removal and pop it
          // back in (re-attaching hover, which the exit had disposed) instead
          // of leaving it shrinking toward zero and then vanishing.
          if (existing.removalTimeoutId !== undefined) {
            clearTimeout(existing.removalTimeoutId)
            timeoutsRef.current.delete(existing.removalTimeoutId)
            existing.removalTimeoutId = undefined
            existing.inner.style.animation = ANNOTATION_POP_IN
            existing.dispose = wireHover(
              existing.el,
              existing.container,
              existing.marker,
              existing.popup,
            )
          }
          // Position is keyed to the id and never changes for a given
          // annotation, so a persisting marker needs no rebuild. Its aria-label
          // and colour can change, though — the label is baked in from `t`, and
          // a caller colouring by something other than severity sees that
          // change on a refetch — so both are refreshed in place (cheap) rather
          // than rebuilding the marker and replaying its pop-in.
          existing.el.setAttribute(
            'aria-label',
            annotationMarkerAriaLabel(t, annotation),
          )
          setAnnotationMarkerColor(existing.el, markerColor(annotation))
          // Resolving a note (or withdrawing that) doesn't move it either.
          setAnnotationMarkerResolved(
            existing.el,
            annotation.resolution !== null,
          )
          nextPopups.push({
            id: annotation.id,
            container: existing.container,
            annotation,
          })
          continue
        }

        // New annotation: build its marker and let it pop in (the inner
        // wrapper starts with ANNOTATION_POP_IN via createAnnotationMarkerElement).
        const { latitude, longitude } = annotation.location

        const el = createAnnotationMarkerElement(
          t,
          markerColor(annotation),
          annotation,
        )
        const inner = el.firstElementChild as HTMLElement

        /**
         * Empty popup shell for one annotation marker. Deliberately just a bare
         * div — the actual content (severity, timestamp, vehicle, transcript,
         * trip link, audio player) is a real React component,
         * `AnnotationPopupContent`, portaled into this node from the caller's own
         * render tree (see `popups` below) rather than built as an HTML string or
         * as hand-assembled DOM. Two things forced that: reusing
         * `TripAnnotationAudioPlayer` (a stateful React component) without
         * reimplementing its logic in vanilla DOM, and the fact that
         * `css={theme => ...}` / pace-web styling needs the app's real
         * ThemeProvider above it in the React tree — a portal from the caller's
         * subtree gets that for free, whereas a second, detached `createRoot`
         * would not.
         */
        const container = document.createElement('div')
        const popup = new maplibregl.Popup({
          // Puts the tip just clear of the marker's white ring.
          offset: ANNOTATION_MARKER_SIZE_PX / 2 + 4,
          closeButton: false,
          closeOnClick: false,
          // MapLibre's `maxWidth` defaults to '240px', which it applies as a
          // `max-width` on `.maplibregl-popup-content` -- and that was winning
          // over `AnnotationPopupContent`'s own width request, cramming the
          // transcript into three lines. Raise it to the content's own fixed
          // width (`ANNOTATION_POPUP_WIDTH_PX`), so that is what governs.
          maxWidth: `${ANNOTATION_POPUP_WIDTH_PX}px`,
          // This is a hover popup, not a click-triggered one -- MapLibre's default
          // of focusing the first focusable element inside it (the audio player's
          // Play button) on open steals keyboard focus the user never asked to
          // move, which is what produced the stray focus ring around Play.
          focusAfterOpen: false,
          // Drops MapLibre's padding so the content's dividers can run edge to
          // edge (see `ensureAnnotationMarkerStyle`).
          className: ANNOTATION_POPUP_CLASS,
        }).setDOMContent(container)

        // The portaled content stays mounted after the popup closes, so a
        // recording that is playing would carry on with no visible controls.
        popup.on('close', () => {
          for (const audio of container.querySelectorAll('audio')) audio.pause()
        })

        const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat([longitude, latitude])
          .setPopup(popup)
          .addTo(currentMap)

        const dispose = wireHover(el, container, marker, popup)

        entries.set(annotation.id, {
          marker,
          el,
          inner,
          container,
          popup,
          dispose,
        })
        nextPopups.push({ id: annotation.id, container, annotation })
      }

      // Swap the portal list only when it actually differs. `setPopups` with a
      // fresh array re-renders the caller, and a caller that builds its
      // `annotations` array inline (a `.filter()` in its JSX rather than a memo)
      // hands over a new identity on every render — which would otherwise be
      // render → effect → setState → render, forever. Compared by reference per
      // entry because that is exactly what decides whether the portaled popup
      // body needs to re-render.
      setPopups(previous =>
        previous.length === nextPopups.length &&
        previous.every(
          (entry, i) =>
            entry.id === nextPopups[i].id &&
            entry.container === nextPopups[i].container &&
            entry.annotation === nextPopups[i].annotation,
        )
          ? previous
          : nextPopups,
      )
    },
    // t, because the marker's aria-label is baked in at build time rather than
    // read on render -- without it, switching language would leave every
    // existing annotation marker announcing itself in the old one. markerColor,
    // so a caller switching colouring (triage turning provenance colours on once
    // its first classification arrives) repaints the markers already drawn.
    [t, markerColor],
  )

  // Only `map` — pointedly not `isMapReady`. Markers and popups are plain DOM
  // attached through `Marker.addTo`, which needs a map but not a loaded style,
  // so making the pins wait on style readiness buys nothing and costs the whole
  // feature if that signal never arrives. The heatmap effect above is the one
  // that genuinely can't run early.
  useEffect(() => {
    if (!map) return
    updateMarkers(map, annotations)
  }, [annotations, map, updateMarkers])

  // Cancel any pending hover/removal timers and tear down every annotation
  // marker when the map goes away (unmount, or the caller replacing its
  // instance). Without this a pop-out removal timer (or a hover-close timer)
  // could fire `remove`/`togglePopup` on a marker after the map is gone, and the
  // DOM marker elements would leak.
  // biome-ignore lint/correctness/useExhaustiveDependencies: tracked for follow-up cleanup
  useEffect(
    () => () => {
      for (const timeoutId of timeoutsRef.current) {
        clearTimeout(timeoutId)
      }
      timeoutsRef.current.clear()
      for (const entry of markersRef.current.values()) {
        entry.dispose()
        entry.marker.remove()
      }
      markersRef.current.clear()
      setPopups([])
    },
    [map],
  )

  const annotationLayer = (
    <>
      {/*
        Escape hatch for the annotation hover popup -- see the matching comment
        in `openPopup` above for the full root-cause write-up. Short version: a
        map container is typically `isolation: isolate`, so it's one atomic
        stacking context and nothing MapLibre renders *inside* it (the popup) can
        be lifted above other things inside it (the numbered waypoint markers) by
        z-index alone. This div must therefore be rendered as a true sibling of
        the map container, filling the exact same box (`inset: 0` against a
        positioned wrapper) at a higher zIndex, so a popup reparented into it on
        open paints above the whole map, markers included, while its
        `map.project()`-derived transform stays pixel-accurate. `pointerEvents:
        'none'` keeps this normally-empty full-bleed layer from blocking clicks
        on the map/controls underneath it -- MapLibre's own
        `.maplibregl-popup-content` CSS already restores `pointer-events: auto`
        for the actual popup card once it's moved in here, so the play/copy/
        trip-link controls inside stay clickable. `isolation: isolate` here too,
        so if more than one popup ever lands in this layer at once they stay
        contained to it rather than reopening the exact same escape problem one
        level up.
      */}
      <div
        ref={popupOverlayRef}
        css={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          isolation: 'isolate',
          zIndex: 250,
        }}
      />
      {
        // Portals, not a second `createRoot`: each popup's container div already
        // lives inside a live MapLibre popup (see `updateMarkers` above), and
        // rendering into it from here — rather than mounting a separate React
        // tree at that node — keeps AnnotationPopupContent inside the caller's
        // real provider tree (theme, i18n, router) instead of a context-less one.
        popups.map(({ id, container, annotation }) =>
          createPortal(
            <AnnotationPopupContent
              annotation={annotation}
              buildTripHref={buildTripHref}
              renderDetails={renderPopupDetails}
            />,
            container,
            id,
          ),
        )
      }
    </>
  )

  return { annotationLayer }
}
